/**
 * CANONICAL MODULE — app-platform/shared/graphErrors.ts
 *
 * Copied byte-for-byte into every app (`src/lib/graphErrors.ts`). It imports nothing. Built for work
 * order W10 (task 2.b) from [design/W10-sync-transport.md](../design/W10-sync-transport.md) §3.1.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * One error contract for every Graph call in the estate, replacing five: bindr's
 * `GraphError{status, detail}`, trakr's `GraphError{status, needsReauth}`, inkling's `RemoteError`,
 * quil's plain `Error` with the code in the string, and contently's ~25 bare `Error`s.
 *
 * ── Rules this file keeps ────────────────────────────────────────────────────────────────────────
 *
 * **No parameter properties.** Three apps set `erasableSyntaxOnly`, which bans
 * `constructor(public x: string)`. Fields are declared, then assigned in the constructor body.
 *
 * **Every `message` is safe for a toast** (bindr, Phase 7 Step 3: a raw `Graph 507: {"error":...`
 * once reached the user), **and still classifiable** by the canonical `errorLog.ts`: each message
 * ends with a short bracketed status such as `[HTTP 503]`, which is what `SYNC_PATTERNS` matches on.
 * The raw body goes in `detail`, for the log only.
 *
 * **`DocConflictError` keeps its name.** errorLog and four callers already know it, and its message
 * always contains `write conflict` (§6.1: bindr's old message matched no sync pattern, so a conflict
 * that escaped its retries was filed as a real error).
 */

/** Graph's `error.code`, from a JSON error body; `undefined` when there is none. */
export function graphErrorCode(body: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed && typeof parsed === 'object') {
      const error = (parsed as { error?: unknown }).error
      if (error && typeof error === 'object') {
        const code = (error as { code?: unknown }).code
        if (typeof code === 'string' && code) return code
      }
    }
  } catch {
    /* not JSON: no code */
  }
  return undefined
}

/**
 * A Graph failure as a sentence a person can act on. App-neutral: it says "OneDrive", never an app
 * name. Two codes contradict their status (403 `quotaLimitReached` is a full drive, 403
 * `activityLimitReached` is a throttle), so the code is read before the status.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function describeGraphFailure(status: number, code?: string): string {
  const tail = ` [HTTP ${status}${code ? ` ${code}` : ''}]`
  if (/quotaLimitReached|insufficientStorage/i.test(code ?? '') || status === 507) {
    return `Your OneDrive is full, so nothing more can be saved to it. Nothing on this device has been lost.${tail}`
  }
  if (/activityLimitReached/i.test(code ?? '') || status === 429 || status === 503) {
    return `OneDrive is busy right now. Try again shortly.${tail}`
  }
  if (status === 401) return `OneDrive did not accept the sign-in.${tail}`
  if (status === 403) return `OneDrive refused access to the app's folder.${tail}`
  if (status === 404) return `That file is no longer in OneDrive.${tail}`
  if (status === 413) return `That file is too large for OneDrive to accept.${tail}`
  if (status === 423) return `That file is locked in OneDrive right now.${tail}`
  if (status >= 500) return `OneDrive is having trouble right now. Try again shortly.${tail}`
  return `OneDrive answered with an error.${tail}`
}

/** Any non-OK Graph response the transport does not map to something more specific. */
export class GraphError extends Error {
  /** HTTP status. */
  readonly status: number
  /** Graph's `error.code` from the body, when it sent one. */
  readonly code: string | undefined
  /** The first 300 characters of the body. For the log, never for a person. */
  readonly detail: string

  constructor(status: number, code: string | undefined, detail = '', message?: string) {
    super(message ?? describeGraphFailure(status, code))
    this.name = 'GraphError'
    this.status = status
    this.code = code
    this.detail = detail.slice(0, 300)
  }
}

/** A throttle (429/503) that outlasted every retry. Carries how long Graph last asked for. */
export class GraphThrottledError extends GraphError {
  /** The last wait the retry policy computed (Retry-After floor, capped), in ms. */
  readonly retryAfterMs: number

  constructor(status: number, code: string | undefined, detail: string, retryAfterMs: number) {
    super(status, code, detail)
    this.name = 'GraphThrottledError'
    this.retryAfterMs = retryAfterMs
  }
}

/** `fetch` itself rejected — offline, DNS, a dropped connection — after every retry. */
export class GraphNetworkError extends Error {
  constructor(cause?: unknown, message = 'Could not reach OneDrive. Check the connection; nothing has been lost.') {
    super(message, cause === undefined ? undefined : { cause })
    this.name = 'GraphNetworkError'
  }
}

export type DocConflictStatus = 412 | 409 | 404
export type DocConflictReason = 'changed' | 'exists' | 'vanished'

const REASON_BY_STATUS: Record<DocConflictStatus, DocConflictReason> = {
  412: 'changed',
  409: 'exists',
  404: 'vanished',
}

/**
 * THE refusal: a conditional write lost a race. Nothing was written.
 *
 * - 412 on `If-Match` → `changed` (another device wrote first)
 * - 409 on `conflictBehavior=fail` → `exists` (the file appeared after all)
 * - 404 on an `If-Match` write → `vanished` (the file the tag named is gone)
 *   — kept for any server that answers 404, but **real Graph does not**: an If-Match write onto a
 *   missing file is accepted and creates it (201). graphClient reports that as
 *   `WrittenItem.recreated`, after the fact (live harness, contently, 2026-10-07).
 *
 * Never retried by the transport; resolution is the caller's (§2.1 — the policy is per app). App
 * subclasses are expected, e.g. bindr's `RecipeConflictError` carrying the newer remote.
 */
export class DocConflictError extends Error {
  /** The app-relative path of the file that was being written. */
  readonly path: string
  readonly status: DocConflictStatus
  readonly reason: DocConflictReason
  /** The file's current tag when the transport could read it cheaply; `null` = absent; `undefined` = unknown. */
  readonly currentTag: string | null | undefined

  constructor(path: string, status: DocConflictStatus = 412, currentTag?: string | null, message?: string) {
    super(message ?? `OneDrive write conflict: ${path} changed on another device first, so nothing was overwritten.`)
    this.name = 'DocConflictError'
    this.path = path
    this.status = status
    this.reason = REASON_BY_STATUS[status]
    this.currentTag = currentTag
  }
}

export function isDocConflict(error: unknown): error is DocConflictError {
  return error instanceof DocConflictError
}

/**
 * The app-root folder Graph resolved is not the one this app expects (§3.3). Fail closed: a
 * `/Apps/Trakr 1` sibling would silently split the web app from trakr's helper, and a renamed
 * registration would show an empty library. Sync stops with this error instead.
 */
export class AppRootMismatchError extends Error {
  readonly expected: string
  readonly actualName: string
  readonly actualParentPath: string

  constructor(expected: string, actualName: string, actualParentPath: string) {
    super(
      `OneDrive gave this app the folder "${actualParentPath}/${actualName}", not "/Apps/${expected}". ` +
        'Syncing is stopped so no data is split between two folders.',
    )
    this.name = 'AppRootMismatchError'
    this.expected = expected
    this.actualName = actualName
    this.actualParentPath = actualParentPath
  }
}
